import React, { useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useLocale, useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import type { InventoryItem } from '../../types/inventory'
import type { ProductionUnit } from '../../types/farm'
import { isMixItem } from '../../utils/feedMix'
import { readFeedRules, SUGGESTED_OUTLET_DO_MIN_MG_L, type UnitFeedRules } from '../../utils/feedRules'
import { readMeasuringPointId } from '../../utils/measuringPoint'
import {
  FEED_PLAN_MODES,
  UNIT_WATER_PARAMETERS,
  feedPlanItems,
  feedPlanMode,
  readFeedPlan,
  readSamplingPlan,
  readWaterParameters,
  samplingPlanLotIdIn,
  type FeedPlanMode,
  type UnitFeedPlan,
  type UnitSamplingPlan,
  type UnitWaterParameter
} from '../../utils/unitPlans'

// Office Plans v0 (pre-validation PR 6): per-unit plans the office sets and
// Crew Mode only reads (Charter O-4) — feed plan ("Dado según plan"),
// planned sampling, water parameters to ask. ASC 4.1.1.9 (feeding plan),
// GLOBALG.A.P. AQ-GFS 20.02.10/11 (sampling), 20.02.18 (water parameters).
// Findings #211: when the crew must not feed, and the oxygen settings.
// Findings #217: where the unit's water is measured, and copying only the rules.

export type UnitCrewPlans = {
  feedPlan: UnitFeedPlan | null
  samplingPlan: UnitSamplingPlan | null
  waterParameters: UnitWaterParameter[] | null
  feedRules: UnitFeedRules | null
  /** Another unit of the facility whose readings this unit uses (findings #217). */
  measuringPointId: string | null
}

const WATER_LABEL_KEY: Record<UnitWaterParameter, TranslationKey> = {
  temperature: 'crewMode.water.parameter.temperature',
  ph: 'crewMode.water.parameter.ph',
  dissolvedOxygen: 'crewMode.water.parameter.dissolvedOxygen',
  salinity: 'crewMode.water.parameter.salinity',
  flow: 'crewMode.water.parameter.flow'
}

const parseNumber = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const inputStyle: React.CSSProperties = { padding: '8px 10px', border: '1px solid #cbd5f5', borderRadius: 10 }

export type UnitCrewPlansEditorProps = {
  unit: ProductionUnit
  feedItems: InventoryItem[]
  activeLots: Array<{ id: string; lotKey: string }>
  disabled: boolean
  onSave: (plans: UnitCrewPlans) => Promise<void>
  /** Other units holding one of this unit's lots (findings #101). */
  copyTargets?: Array<{ id: string; name: string; lotKeys: string[] }>
  onCopy?: (targetIds: string[], copyKgPerDay: boolean) => Promise<void>
  /** The facility's other active units: measuring point choices and rule-copy targets (findings #217). */
  facilityUnits?: Array<{ id: string; name: string }>
  onCopyRules?: (targetIds: string[], copyPoint: boolean) => Promise<void>
}

export default function UnitCrewPlansEditor({ unit, feedItems, activeLots, disabled, onSave, copyTargets = [], onCopy, facilityUnits = [], onCopyRules }: UnitCrewPlansEditorProps) {
  const t = useT()
  const locale = useLocale()
  const feedPlan = readFeedPlan(unit)
  const samplingPlan = readSamplingPlan(unit)
  const waterParameters = readWaterParameters(unit)
  const feedRules = readFeedRules(unit)
  const measuringPointId = readMeasuringPointId(unit)
  const unitName = (id: string | null) => facilityUnits.find((candidate) => candidate.id === id)?.name

  const [open, setOpen] = useState(false)
  const [feedItemId, setFeedItemId] = useState('')
  const [kgPerDay, setKgPerDay] = useState('')
  const [feedingsPerDay, setFeedingsPerDay] = useState('')
  // Findings #182: how the crew records feed here, and an optional second feed.
  const [mode, setMode] = useState<FeedPlanMode>('perFeeding')
  const [secondItemId, setSecondItemId] = useState('')
  const [secondKgPerDay, setSecondKgPerDay] = useState('')
  const [fishCount, setFishCount] = useState('')
  const [measureLength, setMeasureLength] = useState(false)
  const [samplingLotId, setSamplingLotId] = useState('')
  const [water, setWater] = useState<UnitWaterParameter[]>([])
  // Findings #211: stop rules and oxygen settings ('' = not set).
  const [tempRuleOn, setTempRuleOn] = useState(false)
  const [tempMin, setTempMin] = useState('')
  const [tempMax, setTempMax] = useState('')
  const [doRuleOn, setDoRuleOn] = useState(false)
  const [inletDoMin, setInletDoMin] = useState('')
  const [outletDoMin, setOutletDoMin] = useState('')
  const [o2PerKg, setO2PerKg] = useState('')
  const [pointId, setPointId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [copyOpen, setCopyOpen] = useState(false)
  const [copySelection, setCopySelection] = useState<string[]>([])
  const [copyKg, setCopyKg] = useState(false)
  const [copyNotice, setCopyNotice] = useState<string | null>(null)
  const [rulesCopyOpen, setRulesCopyOpen] = useState(false)
  const [rulesSelection, setRulesSelection] = useState<string[]>([])
  const [copyPoint, setCopyPoint] = useState(true)

  const startCopy = () => {
    setCopySelection(copyTargets.map((target) => target.id))
    setCopyKg(false)
    setCopyNotice(null)
    setCopyOpen(true)
  }

  const runCopy = async () => {
    if (!onCopy || copySelection.length === 0) return
    await onCopy(copySelection, copyKg)
    setCopyOpen(false)
    setCopyNotice(t('editFarm.plans.copy.done', { count: copySelection.length }))
  }

  const startRulesCopy = () => {
    setRulesSelection([])
    setCopyPoint(true)
    setCopyNotice(null)
    setCopyOpen(false)
    setRulesCopyOpen(true)
  }

  const runRulesCopy = async () => {
    if (!onCopyRules || rulesSelection.length === 0) return
    await onCopyRules(rulesSelection, copyPoint)
    setRulesCopyOpen(false)
    setCopyNotice(t('editFarm.plans.copyRules.done', { count: rulesSelection.length }))
  }

  const formatKg = (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 3 })
  const formatNumber = (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 })
  const draftOf = (value: number | undefined) => (value === undefined ? '' : String(value))
  const itemName = (id: string) => feedItems.find((item) => item.id === id)?.name

  const startEditing = () => {
    setFeedItemId(feedPlan?.itemId ?? '')
    setKgPerDay(feedPlan ? String(feedPlan.kgPerDay) : '')
    setFeedingsPerDay(feedPlan ? String(feedPlan.feedingsPerDay) : '')
    setMode(feedPlanMode(feedPlan))
    setSecondItemId(feedPlan?.secondItem?.itemId ?? '')
    setSecondKgPerDay(feedPlan?.secondItem ? String(feedPlan.secondItem.kgPerDay) : '')
    setFishCount(samplingPlan ? String(samplingPlan.fishCount) : '')
    setMeasureLength(samplingPlan?.measureLength ?? false)
    // Findings #177: a lot that left this unit shows (and saves) as "any lot".
    setSamplingLotId(samplingPlanLotIdIn(samplingPlan, activeLots.map((lot) => lot.id)))
    setWater(waterParameters)
    setTempRuleOn(feedRules?.tempMinC !== undefined || feedRules?.tempMaxC !== undefined)
    setTempMin(draftOf(feedRules?.tempMinC))
    setTempMax(draftOf(feedRules?.tempMaxC))
    setDoRuleOn(feedRules?.inletDoMinMgL !== undefined)
    setInletDoMin(draftOf(feedRules?.inletDoMinMgL))
    setOutletDoMin(draftOf(feedRules?.outletDoMinMgL))
    setO2PerKg(draftOf(feedRules?.o2KgPerKgFeed))
    setPointId(measuringPointId && unitName(measuringPointId) ? measuringPointId : '')
    setError(null)
    setOpen(true)
  }

  const save = async () => {
    let nextFeedPlan: UnitFeedPlan | null = null
    const anyFeedField = feedItemId || kgPerDay.trim() || (mode === 'perFeeding' && feedingsPerDay.trim()) || secondItemId || secondKgPerDay.trim()
    if (anyFeedField) {
      const kg = parseNumber(kgPerDay)
      // Findings #182: tomas only matter "Por toma"; the other modes keep the
      // last value (or 1) so phones not yet updated still read a complete plan.
      const feedings = mode === 'perFeeding' ? parseNumber(feedingsPerDay) : (parseNumber(feedingsPerDay) ?? 1)
      if (!feedItemId) {
        setError(t('editFarm.plans.error.feed'))
        return
      }
      if (kg === null || kg <= 0) {
        setError(t('editFarm.plans.error.kgPerDay'))
        return
      }
      if (feedings === null || !Number.isInteger(feedings) || feedings < 1 || feedings > 24) {
        setError(t('editFarm.plans.error.feedings'))
        return
      }
      let secondItem: UnitFeedPlan['secondItem']
      if (secondItemId || secondKgPerDay.trim()) {
        const secondKg = parseNumber(secondKgPerDay)
        if (!secondItemId || secondItemId === feedItemId || secondKg === null || secondKg <= 0) {
          setError(t('editFarm.plans.error.secondFeed'))
          return
        }
        secondItem = { itemId: secondItemId, kgPerDay: secondKg }
      }
      nextFeedPlan = {
        itemId: feedItemId,
        kgPerDay: kg,
        feedingsPerDay: feedings,
        ...(mode !== 'perFeeding' ? { mode } : {}),
        ...(secondItem ? { secondItem } : {})
      }
    }

    let nextSamplingPlan: UnitSamplingPlan | null = null
    if (fishCount.trim()) {
      const count = parseNumber(fishCount)
      if (count === null || !Number.isInteger(count) || count < 1 || count > 1000) {
        setError(t('editFarm.plans.error.fishCount'))
        return
      }
      nextSamplingPlan = { fishCount: count, measureLength, ...(samplingLotId ? { lotId: samplingLotId } : {}) }
    }

    const nextFeedRules: UnitFeedRules = {}
    if (tempRuleOn) {
      const min = tempMin.trim() ? parseNumber(tempMin) : undefined
      const max = tempMax.trim() ? parseNumber(tempMax) : undefined
      const inRange = (value: number | null | undefined) => value === undefined || (value !== null && value >= -5 && value <= 40)
      if (!inRange(min) || !inRange(max) || (min === undefined && max === undefined)
        || (typeof min === 'number' && typeof max === 'number' && min >= max)) {
        setError(t('editFarm.plans.error.rules'))
        return
      }
      if (typeof min === 'number') nextFeedRules.tempMinC = min
      if (typeof max === 'number') nextFeedRules.tempMaxC = max
    }
    if (doRuleOn) {
      const min = parseNumber(inletDoMin)
      if (min === null || min < 0.1 || min > 20) {
        setError(t('editFarm.plans.error.rules'))
        return
      }
      nextFeedRules.inletDoMinMgL = min
    }
    if (outletDoMin.trim()) {
      const value = parseNumber(outletDoMin)
      if (value === null || value < 0.1 || value > 20) {
        setError(t('editFarm.plans.error.oxygen'))
        return
      }
      nextFeedRules.outletDoMinMgL = value
    }
    if (o2PerKg.trim()) {
      const value = parseNumber(o2PerKg)
      if (value === null || value < 0.05 || value > 1) {
        setError(t('editFarm.plans.error.oxygen'))
        return
      }
      nextFeedRules.o2KgPerKgFeed = value
    }

    setError(null)
    await onSave({
      feedPlan: nextFeedPlan,
      samplingPlan: nextSamplingPlan,
      waterParameters: water.length > 0 ? UNIT_WATER_PARAMETERS.filter((parameter) => water.includes(parameter)) : null,
      feedRules: Object.keys(nextFeedRules).length > 0 ? nextFeedRules : null,
      measuringPointId: pointId || null
    })
    setOpen(false)
  }

  const planFeeds = feedPlanItems(feedPlan)
    .map((entry) => `${formatKg(entry.kgPerDay)} kg ${itemName(entry.itemId) ?? t('editFarm.plans.missingFeed')}`)
    .join(' + ')
  const feedSummary = feedPlan
    ? (feedPlanMode(feedPlan) === 'perFeeding'
      ? t('editFarm.plans.feedSummaryPerFeeding', { feeds: planFeeds, feedings: feedPlan.feedingsPerDay })
      : t('editFarm.plans.feedSummaryMode', { feeds: planFeeds, mode: t(`editFarm.plans.mode.${feedPlanMode(feedPlan)}`) }))
    : t('editFarm.plans.feedNone')
  const samplingSummary = samplingPlan
    ? t(samplingPlan.measureLength ? 'editFarm.plans.samplingSummaryLength' : 'editFarm.plans.samplingSummary', {
      count: samplingPlan.fishCount
    })
    : t('editFarm.plans.samplingNone')
  const waterSummary = waterParameters.length > 0
    ? waterParameters.map((parameter) => t(WATER_LABEL_KEY[parameter])).join(', ')
    : t('editFarm.plans.waterAll')
  const ruleParts: string[] = []
  if (feedRules?.tempMinC !== undefined && feedRules.tempMaxC !== undefined) {
    ruleParts.push(t('editFarm.plans.rulesSummary.tempRange', { min: formatNumber(feedRules.tempMinC), max: formatNumber(feedRules.tempMaxC) }))
  } else if (feedRules?.tempMinC !== undefined) {
    ruleParts.push(t('editFarm.plans.rulesSummary.tempBelow', { min: formatNumber(feedRules.tempMinC) }))
  } else if (feedRules?.tempMaxC !== undefined) {
    ruleParts.push(t('editFarm.plans.rulesSummary.tempAbove', { max: formatNumber(feedRules.tempMaxC) }))
  }
  if (feedRules?.inletDoMinMgL !== undefined) {
    ruleParts.push(t('editFarm.plans.rulesSummary.do', { min: formatNumber(feedRules.inletDoMinMgL) }))
  }
  const rulesSummary = ruleParts.length > 0 ? ruleParts.join(' · ') : t('editFarm.plans.rulesSummary.none')
  const pointSummary = measuringPointId
    ? (unitName(measuringPointId) ?? t('editFarm.plans.point.missing'))
    : t('editFarm.plans.point.self')
  const pointNameForCopy = unitName(measuringPointId) ?? unit.name
  const feedPlanStale = Boolean(feedPlan && feedPlanItems(feedPlan).some((entry) => !itemName(entry.itemId)))

  return (
    <div data-testid={`edit-farm-pu-plans-${unit.id}`} style={{ marginTop: 8, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: 2 }}>
          <strong style={{ fontSize: 12 }}>{t('editFarm.plans.title')}</strong>
          <span data-testid={`edit-farm-pu-plans-feed-${unit.id}`}>
            {t('editFarm.plans.feedLabel')}: {feedSummary}
            {feedPlanStale ? <span style={{ color: '#b91c1c', fontWeight: 700 }}> · {t('editFarm.plans.staleFeed')}</span> : null}
          </span>
          <span>{t('editFarm.plans.samplingLabel')}: {samplingSummary}</span>
          <span>{t('editFarm.plans.waterLabel')}: {waterSummary}</span>
          <span data-testid={`edit-farm-pu-plans-point-${unit.id}`}>{t('editFarm.plans.point.label')}: {pointSummary}</span>
          <span data-testid={`edit-farm-pu-plans-rules-${unit.id}`}>{t('editFarm.plans.rulesLabel')}: {rulesSummary}</span>
        </div>
        {!open ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <GradientButton
              size="xs"
              variant="info"
              onClick={startEditing}
              disabled={disabled}
              data-testid={`edit-farm-pu-plans-edit-${unit.id}`}
            >
              {t('editFarm.plans.edit')}
            </GradientButton>
            {onCopy && copyTargets.length > 0 && !copyOpen ? (
              <GradientButton
                size="xs"
                variant="secondary"
                onClick={startCopy}
                disabled={disabled}
                data-testid={`edit-farm-pu-plans-copy-${unit.id}`}
              >
                {t('editFarm.plans.copy.button')}
              </GradientButton>
            ) : null}
            {onCopyRules && facilityUnits.length > 0 && !rulesCopyOpen ? (
              <GradientButton
                size="xs"
                variant="secondary"
                onClick={startRulesCopy}
                disabled={disabled}
                data-testid={`edit-farm-pu-plans-copy-rules-${unit.id}`}
              >
                {t('editFarm.plans.copyRules.button')}
              </GradientButton>
            ) : null}
          </div>
        ) : null}
      </div>

      {copyNotice ? <div role="status" style={{ marginTop: 6, color: '#065f46', fontSize: 12 }}>{copyNotice}</div> : null}

      {copyOpen && !open ? (
        <div data-testid={`edit-farm-pu-plans-copy-panel-${unit.id}`} style={{ display: 'grid', gap: 8, marginTop: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
          <strong style={{ fontSize: 12 }}>{t('editFarm.plans.copy.title')}</strong>
          {copyTargets.map((target) => (
            <label key={target.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={copySelection.includes(target.id)}
                onChange={(event) => setCopySelection((prev) => (event.target.checked
                  ? [...prev, target.id]
                  : prev.filter((id) => id !== target.id)))}
              />
              {target.name} <span style={{ color: '#64748b' }}>({target.lotKeys.join(', ')})</span>
            </label>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={copyKg} onChange={(event) => setCopyKg(event.target.checked)} data-testid={`edit-farm-pu-plans-copy-kg-${unit.id}`} />
            {t('editFarm.plans.copy.kg')}
          </label>
          <span style={{ color: '#6b7280', fontSize: 12 }}>{t('editFarm.plans.copy.help')}</span>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GradientButton size="xs" variant="secondary" onClick={() => setCopyOpen(false)}>
              {t('common.cancel')}
            </GradientButton>
            <GradientButton
              size="xs"
              variant="success"
              onClick={() => void runCopy()}
              disabled={disabled || copySelection.length === 0}
              data-testid={`edit-farm-pu-plans-copy-run-${unit.id}`}
            >
              {t('editFarm.plans.copy.run', { count: copySelection.length })}
            </GradientButton>
          </div>
        </div>
      ) : null}

      {rulesCopyOpen && !open ? (
        <div data-testid={`edit-farm-pu-plans-copy-rules-panel-${unit.id}`} style={{ display: 'grid', gap: 8, marginTop: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
          <strong style={{ fontSize: 12 }}>{t('editFarm.plans.copyRules.title', { rules: rulesSummary })}</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <GradientButton size="xs" variant="secondary" onClick={() => setRulesSelection(facilityUnits.map((target) => target.id))} data-testid={`edit-farm-pu-plans-copy-rules-all-${unit.id}`}>
              {t('editFarm.plans.copyRules.all')}
            </GradientButton>
            <GradientButton size="xs" variant="secondary" onClick={() => setRulesSelection([])}>
              {t('editFarm.plans.copyRules.none')}
            </GradientButton>
          </div>
          <div style={{ display: 'flex', gap: '4px 14px', flexWrap: 'wrap' }}>
            {facilityUnits.map((target) => (
              <label key={target.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  data-testid={`edit-farm-pu-plans-copy-rules-target-${target.id}`}
                  checked={rulesSelection.includes(target.id)}
                  onChange={(event) => setRulesSelection((prev) => (event.target.checked
                    ? [...prev, target.id]
                    : prev.filter((id) => id !== target.id)))}
                />
                {target.name}
              </label>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={copyPoint} onChange={(event) => setCopyPoint(event.target.checked)} data-testid={`edit-farm-pu-plans-copy-rules-point-${unit.id}`} />
            {t('editFarm.plans.copyRules.point', { unit: pointNameForCopy })}
          </label>
          <span style={{ color: '#6b7280', fontSize: 12 }}>{t('editFarm.plans.copyRules.help')}</span>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GradientButton size="xs" variant="secondary" onClick={() => setRulesCopyOpen(false)}>
              {t('common.cancel')}
            </GradientButton>
            <GradientButton
              size="xs"
              variant="success"
              onClick={() => void runRulesCopy()}
              disabled={disabled || rulesSelection.length === 0}
              data-testid={`edit-farm-pu-plans-copy-rules-run-${unit.id}`}
            >
              {t('editFarm.plans.copy.run', { count: rulesSelection.length })}
            </GradientButton>
          </div>
        </div>
      ) : null}

      {open ? (
        <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <legend style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{t('editFarm.plans.feedLabel')}</legend>
            <label style={{ display: 'grid', gap: 2 }}>
              <span>{t('editFarm.plans.feed')}</span>
              <select
                data-testid={`edit-farm-pu-plans-feed-item-${unit.id}`}
                value={feedItemId}
                onChange={(event) => setFeedItemId(event.target.value)}
                style={inputStyle}
              >
                <option value="">{t('editFarm.plans.noPlan')}</option>
                {feedItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {isMixItem(item) ? `${item.name} (${t('crewMode.feed.mixBadge')})` : item.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 2 }}>
              <span>{t('editFarm.plans.kgPerDay')}</span>
              <input
                data-testid={`edit-farm-pu-plans-kg-${unit.id}`}
                inputMode="decimal"
                value={kgPerDay}
                onChange={(event) => setKgPerDay(event.target.value)}
                style={{ ...inputStyle, width: 110 }}
              />
            </label>
            {mode === 'perFeeding' ? (
              <label style={{ display: 'grid', gap: 2 }}>
                <span>{t('editFarm.plans.feedingsPerDay')}</span>
                <input
                  data-testid={`edit-farm-pu-plans-feedings-${unit.id}`}
                  inputMode="numeric"
                  value={feedingsPerDay}
                  onChange={(event) => setFeedingsPerDay(event.target.value)}
                  style={{ ...inputStyle, width: 90 }}
                />
              </label>
            ) : null}
          </fieldset>

          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'grid', gap: 2 }}>
              <span>{t('editFarm.plans.secondFeed')}</span>
              <select
                data-testid={`edit-farm-pu-plans-feed-item-2-${unit.id}`}
                value={secondItemId}
                onChange={(event) => setSecondItemId(event.target.value)}
                style={inputStyle}
              >
                <option value="">{t('editFarm.plans.noSecondFeed')}</option>
                {feedItems.filter((item) => item.id !== feedItemId).map((item) => (
                  <option key={item.id} value={item.id}>
                    {isMixItem(item) ? `${item.name} (${t('crewMode.feed.mixBadge')})` : item.name}
                  </option>
                ))}
              </select>
            </label>
            {secondItemId ? (
              <label style={{ display: 'grid', gap: 2 }}>
                <span>{t('editFarm.plans.kgPerDay')}</span>
                <input
                  data-testid={`edit-farm-pu-plans-kg-2-${unit.id}`}
                  inputMode="decimal"
                  value={secondKgPerDay}
                  onChange={(event) => setSecondKgPerDay(event.target.value)}
                  style={{ ...inputStyle, width: 110 }}
                />
              </label>
            ) : null}
          </fieldset>

          <fieldset data-testid={`edit-farm-pu-plans-mode-${unit.id}`} style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
            <legend style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{t('editFarm.plans.mode')}</legend>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {FEED_PLAN_MODES.map((candidate) => (
                <label key={candidate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name={`feed-plan-mode-${unit.id}`}
                    data-testid={`edit-farm-pu-plans-mode-${candidate}-${unit.id}`}
                    checked={mode === candidate}
                    onChange={() => setMode(candidate)}
                  />
                  {t(`editFarm.plans.mode.${candidate}`)}
                </label>
              ))}
            </div>
            <span style={{ color: '#6b7280', fontSize: 12 }}>{t('editFarm.plans.mode.help')}</span>
          </fieldset>

          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <legend style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{t('editFarm.plans.samplingLabel')}</legend>
            <label style={{ display: 'grid', gap: 2 }}>
              <span>{t('editFarm.plans.fishCount')}</span>
              <input
                data-testid={`edit-farm-pu-plans-fish-${unit.id}`}
                inputMode="numeric"
                value={fishCount}
                onChange={(event) => setFishCount(event.target.value)}
                style={{ ...inputStyle, width: 90 }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 36 }}>
              <input
                type="checkbox"
                data-testid={`edit-farm-pu-plans-length-${unit.id}`}
                checked={measureLength}
                onChange={(event) => setMeasureLength(event.target.checked)}
              />
              {t('editFarm.plans.measureLength')}
            </label>
            {activeLots.length > 0 ? (
              <label style={{ display: 'grid', gap: 2 }}>
                <span>{t('editFarm.plans.samplingLot')}</span>
                <select value={samplingLotId} onChange={(event) => setSamplingLotId(event.target.value)} style={inputStyle}>
                  <option value="">{t('editFarm.plans.anyLot')}</option>
                  {activeLots.map((lot) => (
                    <option key={lot.id} value={lot.id}>{lot.lotKey}</option>
                  ))}
                </select>
              </label>
            ) : null}
          </fieldset>

          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <legend style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{t('editFarm.plans.waterLabel')}</legend>
            {UNIT_WATER_PARAMETERS.map((parameter) => (
              <label key={parameter} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  data-testid={`edit-farm-pu-plans-water-${parameter}-${unit.id}`}
                  checked={water.includes(parameter)}
                  onChange={(event) => setWater((prev) => (event.target.checked
                    ? [...prev, parameter]
                    : prev.filter((candidate) => candidate !== parameter)))}
                />
                {t(WATER_LABEL_KEY[parameter])}
              </label>
            ))}
            <span style={{ color: '#6b7280', fontSize: 12 }}>{t('editFarm.plans.waterHelp')}</span>
          </fieldset>

          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
            <legend style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{t('editFarm.plans.point.label')}</legend>
            <select
              data-testid={`edit-farm-pu-plans-point-select-${unit.id}`}
              value={pointId}
              onChange={(event) => setPointId(event.target.value)}
              style={{ ...inputStyle, maxWidth: 260 }}
            >
              <option value="">{t('editFarm.plans.point.self')}</option>
              {facilityUnits.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>{candidate.name}</option>
              ))}
            </select>
            <span style={{ color: '#6b7280', fontSize: 12 }}>{t('editFarm.plans.point.help')}</span>
          </fieldset>

          <fieldset data-testid={`edit-farm-pu-plans-rules-editor-${unit.id}`} style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
            <legend style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{t('editFarm.plans.rulesLabel')}</legend>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  data-testid={`edit-farm-pu-plans-rule-temp-${unit.id}`}
                  checked={tempRuleOn}
                  onChange={(event) => setTempRuleOn(event.target.checked)}
                />
                {t('editFarm.plans.rules.temp')}
              </label>
              <input
                aria-label={t('editFarm.plans.rules.tempMin')}
                data-testid={`edit-farm-pu-plans-rule-temp-min-${unit.id}`}
                inputMode="decimal"
                value={tempMin}
                disabled={!tempRuleOn}
                onChange={(event) => setTempMin(event.target.value)}
                style={{ ...inputStyle, width: 70 }}
              />
              <span>{t('editFarm.plans.rules.tempTo')}</span>
              <input
                aria-label={t('editFarm.plans.rules.tempMax')}
                data-testid={`edit-farm-pu-plans-rule-temp-max-${unit.id}`}
                inputMode="decimal"
                value={tempMax}
                disabled={!tempRuleOn}
                onChange={(event) => setTempMax(event.target.value)}
                style={{ ...inputStyle, width: 70 }}
              />
              <span>°C</span>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  data-testid={`edit-farm-pu-plans-rule-do-${unit.id}`}
                  checked={doRuleOn}
                  onChange={(event) => setDoRuleOn(event.target.checked)}
                />
                {t('editFarm.plans.rules.do')}
              </label>
              <input
                aria-label={t('editFarm.plans.rules.do')}
                data-testid={`edit-farm-pu-plans-rule-do-min-${unit.id}`}
                inputMode="decimal"
                value={inletDoMin}
                disabled={!doRuleOn}
                onChange={(event) => setInletDoMin(event.target.value)}
                style={{ ...inputStyle, width: 70 }}
              />
              <span>mg/L</span>
            </div>
            <span style={{ color: '#6b7280', fontSize: 12 }}>{t('editFarm.plans.rules.help')}</span>
          </fieldset>

          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <legend style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{t('editFarm.plans.oxygenLabel')}</legend>
            <label style={{ display: 'grid', gap: 2 }}>
              <span>{t('editFarm.plans.oxygen.outletMin')}</span>
              <input
                data-testid={`edit-farm-pu-plans-outlet-do-${unit.id}`}
                inputMode="decimal"
                value={outletDoMin}
                placeholder={formatNumber(SUGGESTED_OUTLET_DO_MIN_MG_L)}
                onChange={(event) => setOutletDoMin(event.target.value)}
                style={{ ...inputStyle, width: 90 }}
              />
              <span style={{ color: '#94a3b8', fontSize: 12 }}>
                {t('editFarm.plans.oxygen.suggested', { value: formatNumber(SUGGESTED_OUTLET_DO_MIN_MG_L) })}
              </span>
            </label>
            <label style={{ display: 'grid', gap: 2 }}>
              <span>{t('editFarm.plans.oxygen.perKg')}</span>
              <input
                data-testid={`edit-farm-pu-plans-o2-per-kg-${unit.id}`}
                inputMode="decimal"
                value={o2PerKg}
                placeholder={formatNumber(0.25)}
                onChange={(event) => setO2PerKg(event.target.value)}
                style={{ ...inputStyle, width: 90 }}
              />
              <span style={{ fontSize: 12 }}>&nbsp;</span>
            </label>
            <span style={{ color: '#6b7280', fontSize: 12, flexBasis: '100%' }}>{t('editFarm.plans.oxygen.help')}</span>
          </fieldset>

          {error ? <div role="alert" style={{ color: '#b91c1c', fontWeight: 700 }}>{error}</div> : null}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GradientButton size="xs" variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </GradientButton>
            <GradientButton
              size="xs"
              variant="success"
              onClick={() => void save()}
              disabled={disabled}
              data-testid={`edit-farm-pu-plans-save-${unit.id}`}
            >
              {t('common.save')}
            </GradientButton>
          </div>
        </div>
      ) : null}
    </div>
  )
}
